import React, { useState, useEffect } from "react";
import {
  RowsContainer,
  Row,
  InputContainer,
  SelectContainer,
  IconContainer,
  ButtonsContainer,
  ButtonGroup,
  CancelButton
} from "./index.styles";
import {
  RotaButton,
  RotaButtonIcon,
  RotaInput,
  RotaDropdown
} from "@teamrota/rota-design";

import { useContacts } from "./hooks/useContacts";
import useBulkMutateAccountContacts from "./hooks/useUpdateContacts";

const PRIORITY_OPTIONS = [
  { label: "Normal", value: "normal" },
  { label: "High", value: "high" },
  { label: "Maximum", value: "maximum" }
];

const ContactsPanel = ({ accountId }) => {
  const { contacts, refetch } = useContacts(accountId);

  const { bulkMutateAccountContacts } = useBulkMutateAccountContacts();

  const [updatedContacts, setUpdatedContacts] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    setUpdatedContacts(
      contacts.map(contact => ({ ...contact, isNew: false, isDeleted: false }))
    );
  }, [contacts]);

  const validateEmail = email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateContacts = contactsToValidate => {
    const errors = {};
    let hasErrors = false;
    const emailsSeen = new Set();

    contactsToValidate.forEach(contact => {
      if (contact.isDeleted) return;

      errors[contact.id] = {};

      if (!contact.name || contact.name.length < 5) {
        errors[contact.id].name = "Name must be at least 5 characters";
        hasErrors = true;
      }

      if (!contact.email || !validateEmail(contact.email)) {
        errors[contact.id].email = "Please enter a valid email address";
        hasErrors = true;
      } else {
        const emailLower = contact.email.toLowerCase();
        if (emailsSeen.has(emailLower)) {
          errors[contact.id].email = "Duplicate email address";
          hasErrors = true;
        }
        emailsSeen.add(emailLower);
      }
    });

    return { errors, hasErrors };
  };

  const handleAddContact = () => {
    setUpdatedContacts([
      ...updatedContacts,
      {
        // Temporary id, will be ignored on the backend due to the isNew flag
        id: Math.random()
          .toString(36)
          .substring(2, 15),
        name: "",
        email: "",
        priority: "normal",
        isNew: true,
        isDeleted: false
      }
    ]);
  };

  const handleContactChange = (contactId, field, value) => {
    setUpdatedContacts(prev =>
      prev.map(contact =>
        contact.id === contactId ? { ...contact, [field]: value } : contact
      )
    );

    // Validate after change
    const updatedList = updatedContacts.map(contact =>
      contact.id === contactId ? { ...contact, [field]: value } : contact
    );
    const { errors } = validateContacts(updatedList);
    setValidationErrors(errors);
  };

  const handleDeleteContact = contactId => {
    setUpdatedContacts(prev =>
      prev.map(contact =>
        contact.id === contactId ? { ...contact, isDeleted: true } : contact
      )
    );
  };

  const handleSave = async () => {
    const activeContacts = updatedContacts.filter(c => !c.isDeleted);
    const { errors, hasErrors } = validateContacts(activeContacts);
    setValidationErrors(errors);

    if (!hasErrors) {
      try {
        await bulkMutateAccountContacts({
          accountId,
          contacts: updatedContacts.map(
            ({ id, name, email, priority, isNew, isDeleted }) => ({
              id,
              name,
              email,
              priority: priority || "normal",
              isNew,
              isDeleted
            })
          )
        });
        setIsEditMode(false);
        refetch();
      } catch (error) {
        console.error("Failed to update contacts:", error);
      }
    }
  };

  const handleCancel = () => {
    setUpdatedContacts(
      contacts.map(contact => ({ ...contact, isNew: false, isDeleted: false }))
    );
    setValidationErrors({});
    setIsEditMode(false);
  };

  // Check if any contacts have been added, deleted or changed
  const isUpdatedContactsDifferent = () => {
    if (contacts.length !== updatedContacts.length) return true;

    if (updatedContacts.filter(contact => contact.isNew).length >= 1)
      return true;
    if (updatedContacts.filter(contact => contact.isDeleted).length >= 1)
      return true;

    return updatedContacts.some(updatedContact => {
      const originalContact = contacts.find(c => c.id === updatedContact.id);
      if (!originalContact) return true;
      const isNameDifferent = updatedContact.name !== originalContact.name;
      const isEmailDifferent = updatedContact.email !== originalContact.email;
      const isPriorityDifferent =
        updatedContact.priority !== originalContact.priority;
      return isNameDifferent || isEmailDifferent || isPriorityDifferent;
    });
  };

  const isNoErrors = Object.values(validationErrors).every(
    errors => !errors.name && !errors.email
  );
  const isSaveEnabled = isUpdatedContactsDifferent() && isNoErrors;

  const activeContacts = updatedContacts.filter(contact => !contact.isDeleted);
  const canDeleteContact = activeContacts.length > 1;

  return (
    <div>
      <RowsContainer>
        {activeContacts.map(contact => (
          <Row key={contact.id}>
            <InputContainer>
              <RotaInput
                label="Contact name"
                value={contact.name}
                onChange={event => {
                  handleContactChange(contact.id, "name", event.target.value);
                }}
                disabled={!isEditMode}
                isError={!!validationErrors[contact.id]?.name}
                errorMessage={validationErrors[contact.id]?.name}
              />
            </InputContainer>

            <InputContainer>
              <RotaInput
                label="Contact email"
                value={contact.email}
                onChange={event => {
                  handleContactChange(contact.id, "email", event.target.value);
                }}
                disabled={!isEditMode}
                isError={!!validationErrors[contact.id]?.email}
                errorMessage={validationErrors[contact.id]?.email}
              />
            </InputContainer>

            <SelectContainer>
              <RotaDropdown
                label="Priority"
                placeholder="Select priority"
                options={PRIORITY_OPTIONS}
                value={contact.priority || "normal"}
                isDisabled={!isEditMode}
                onChange={value => {
                  handleContactChange(contact.id, "priority", value);
                }}
              />
            </SelectContainer>

            {isEditMode && (
              <IconContainer>
                <RotaButtonIcon
                  icon="delete"
                  onClick={() => {
                    handleDeleteContact(contact.id);
                  }}
                  disabled={!canDeleteContact}
                />
              </IconContainer>
            )}
          </Row>
        ))}
        <ButtonsContainer>
          {!isEditMode && (
            <>
              <div />
              <RotaButton
                variant="outlined"
                onClick={() => setIsEditMode(true)}
              >
                Edit
              </RotaButton>
            </>
          )}

          {isEditMode && (
            <>
              <RotaButton variant="outlined" onClick={handleAddContact}>
                Add another
              </RotaButton>

              <ButtonGroup>
                <CancelButton onClick={handleCancel}>Cancel</CancelButton>

                <RotaButton
                  disabled={!isSaveEnabled}
                  variant="primary"
                  onClick={handleSave}
                >
                  Save
                </RotaButton>
              </ButtonGroup>
            </>
          )}
        </ButtonsContainer>
      </RowsContainer>
    </div>
  );
};

export default ContactsPanel;
